export const paymentDetailSchema = {
  title: "Payment Details",
  type: "object",
  required: ["paymentDetailsSection0"],
  properties: {
    paymentDetailsSection0: {
      type: "object",
      title: "Payment Details",
      required: ["baseRate", "serviceType", "signatureInfo"],
      properties: {
        baseRate: {
          type: "number",
          title: "Base rate (Per Hour)",
          minimum: 34,
          maximum: 1000,
          errorMessage: {
            minimum: "Base rate must be at least $34/hr",
            maximum: "Base rate cannot exceed $1000/hr",
            required: "Please enter base rate",
          },
        },
        serviceType: {
          type: "string",
          title: "Service Type",
          enum: ["personal", "couple", "companion", "skilled"],
          minLength: 1,
          errorMessage: {
            required: "Please select service type",
            minLength: "Please select service type",
          },
        },
        signatureInfo: {
          type: "object",
          title: "Payment Agreement Signature",
          required: ["signature", "NameOfSigner"],
          properties: {
            signature: {
              type: "string",
              title:
                "I (we) have given this authority to Cambridge Caregivers to charge the card on file or to bank draft my (our) account for the payment of my (our) biweekly invoice. I (we) understand this authority shall remain in full force and effect until written notification of termination is received from me (us) and Cambridge Caregivers and the banking institution have a reasonable opportunity to act upon it. I (we) understand that nothing contained in this Authorization shall serve to reduce my (our) obligation to pay my (our) invoice and services may be discontinued should I (we) fail to have sufficient funds in the above referenced credit card or account to cover the amount of the bill.",
              errorMessage: {
                required: "Signature is required",
              },
            },
            NameOfSigner: {
              type: "string",
              title: "Name of Signer",
              maxLength: 50,
              errorMessage: {
                required: "Name of signer is required",
                maxLength: "Name of signer must be under 50 characters",
              },
            },
          },
        },
        paymentMethod: {
          type: "string",
          title: "Payment Method",
          default: "bank",
          oneOf: [
            {
              const: "bank",
              title: "ACH Transfer (Recommended)",
              description: "Direct bank transfer with no processing fee",
            },
            {
              const: "credit",
              title: "Credit Card",
              description: "(5% processing fee applies)",
            },
          ],
          errorMessage: {
            required: "Payment method is required",
          },
        },
      },
      dependencies: {
        paymentMethod: {
          oneOf: [
            {
              properties: {
                paymentMethod: { const: "bank" },
                bankDetails: {
                  type: "object",
                  title: "Bank Details",
                  required: [
                    "bankName",
                    "accountHolderName",
                    "routingNumber",
                    "accountNumber",
                    "authorizationToTransfer",
                  ],
                  properties: {
                    bankName: {
                      type: "string",
                      title: "Depository Bank Name",
                      minLength: 2,
                      maxLength: 100,
                      errorMessage: {
                        required: "Please enter depository bank name",
                        minLength: "Bank name must be at least 2 characters",
                        maxLength: "Bank name must be under 100 characters",
                      },
                    },
                    accountHolderName: {
                      type: "string",
                      title: "Account Holder Name",
                      minLength: 2,
                      maxLength: 100,
                      errorMessage: {
                        required: "Please enter account holder name",
                        minLength: "Name must be at least 2 characters",
                        maxLength: "Name must be under 100 characters",
                      },
                    },
                    routingNumber: {
                      type: "string",
                      title: "Routing Number",
                      pattern: "^[0-9]{9}$",
                      errorMessage: {
                        required: "Please enter routing number",
                        pattern: "Routing number must be exactly 9 digits",
                      },
                    },
                    accountNumber: {
                      type: "string",
                      title: "Account Number",
                      pattern: "^[0-9]{4,17}$",
                      errorMessage: {
                        required: "Please enter account number",
                        pattern: "Account number must be 4–17 digits only",
                      },
                    },
                    authorizationToTransfer: {
                      type: "boolean",
                      title:
                        "Authorization to Transfer to Cambridge Caregivers, LLC",
                      description:
                        "I hereby authorize Cambridge Caregivers, LLC to draw upon the above account for payment of obligations owed by Client under this Agreement, I authorize the above Depository Bank to approve transfers initiated by Cambridge Caregivers, LLC for payment on behalf of Client, and I agree to execute any additional documents as may be required by the Depository Bank for such authorization.",
                      errorMessage: {
                        required:
                          "You must authorize the transfer to Cambridge Caregivers, LLC",
                      },
                    },
                  },
                },
              },
              required: ["bankDetails"],
            },

            {
              properties: {
                paymentMethod: { const: "credit" },
                creditCardDetails: {
                  type: "object",
                  title: "Credit Card Details",
                  required: [
                    "cardHolderName",
                    "cardNumber",
                    "expirationDate",
                    "cvv",
                    "zipCode",
                    "feeAcknowledgment",
                  ],
                  properties: {
                    cardHolderName: {
                      type: "string",
                      title: "Card Holder Full Name",
                      minLength: 2,
                      maxLength: 100,
                      errorMessage: {
                        required: "Card holder name is required",
                        minLength: "Name must be at least 2 characters",
                        maxLength: "Name must be under 100 characters",
                      },
                    },
                    cardNumber: {
                      type: "string",
                      title: "Card Number",
                      pattern: "^[0-9]{13,19}$",
                      errorMessage: {
                        required: "Card number is required",
                        pattern: "Card number must be between 13 to 19 digits",
                      },
                    },
                    expirationDate: {
                      type: "string",
                      title: "Expiration Date (MM/YY)",
                      pattern: "^(0[1-9]|1[0-2])/[0-9]{2}$",
                      errorMessage: {
                        required: "Expiration date is required",
                        pattern: "Use format MM/YY (e.g., 12/25)",
                      },
                    },
                    cvv: {
                      type: "string",
                      title: "CVV",
                      pattern: "^[0-9]{3,4}$",
                      errorMessage: {
                        required: "CVV is required",
                        pattern: "CVV must be 3 or 4 digits",
                      },
                    },
                    zipCode: {
                      type: "string",
                      title: "ZIP Code",
                      pattern: "^[0-9]{5}$",
                      errorMessage: {
                        required: "ZIP code is required",
                        pattern: "ZIP code must be exactly 5 digits",
                      },
                    },
                    feeAcknowledgment: {
                      type: "boolean",
                      title: "Credit Card Processing Fee Acknowledgment",
                      description:
                        "I acknowledge that I will pay a 5% recurring fee for using a credit card",
                      errorMessage: {
                        required: "You must acknowledge the processing fee",
                      },
                    },
                  },
                },
              },
              required: ["creditCardDetails"],
            },
          ],
        },
      },
    },
  },
};

export const paymentDetailUiSchema = {
  paymentDetailsSection0: {
    "ui:order": [
      "baseRate",
      "serviceType",
      "signatureInfo",
      "paymentMethod",
      "bankDetails",
      "creditCardDetails",
      "checkDetails",
    ],
    baseRate: {
      "ui:widget": "AdornmentTextWidget",
      "ui:options": {
        inputType: "text",
        allowDecimal: true,
        adornment: "$",
        min: 0,
        maxLength: 7,
        maxDecimalPlaces: 2,
        max: 1000,
      },
    },
    serviceType: {
      "ui:md": 12,
      "ui:widget": "select",
      "ui:enumNames": [
        "Personal Care",
        "Couple Care (Please complete document for both individuals)",
        "Companion Care",
        "Skilled Care (LVN, RN, EMT-P)",
      ],
    },
    signatureInfo: {
      signature: {
        "ui:widget": "SignatureWidget",
        "ui:options": {
          label: false,
        },
        "ui:md": 12,
      },
      NameOfSigner: {
        "ui:md": 6,
      },
    },
    paymentMethod: {
      "ui:widget": "RadioDescriptionWidget",
      "ui:options": {
        label: false,
        description: "Choose how you would like to provide payment",
        enumOptions: [
          {
            label: "Bank (ACH/EFT)",
            value: "bank",
          },
          {
            label: "Credit Card",
            value: "credit",
          },
        ],
      },
    },

    bankDetails: {
      "ui:field": "inlineField",
      bankName: { "ui:md": 6 },
      accountHolderName: { "ui:md": 6 },
      routingNumber: { "ui:md": 6 },
      accountNumber: { "ui:md": 6 },
      authorizationToTransfer: {
        "ui:md": 12,
        "ui:widget": "CardCheckboxWidget",
        "ui:options": {
          label: false,
        },
      },
    },
    creditCardDetails: {
      "ui:field": "inlineField",
      cardHolderName: { "ui:md": 6 },
      cardNumber: {
        "ui:md": 6,
        "ui:widget": "NumberWidget",
        "ui:options": {
          hideError: false,
          digitsOnly: true,
          minLength: 13,
          maxLength: 19,
          inputMode: "numeric",
        },
      },
      expirationDate: { "ui:md": 3 },
      cvv: {
        "ui:md": 3,
        "ui:widget": "NumberWidget",
        "ui:options": {
          hideError: false,
          digitsOnly: true,
          minLength: 3,
          maxLength: 4,
          inputMode: "numeric",
        },
      },
      zipCode: {
        "ui:md": 3,
        "ui:widget": "NumberWidget",
        "ui:options": {
          hideError: false,
          digitsOnly: true,
          maxLength: 5,
          inputMode: "numeric",
        },
      },
      feeAcknowledgment: {
        "ui:md": 12,
        "ui:widget": "CardCheckboxWidget",
        "ui:options": {
          label: false,
        },
      },
    },
    checkDetails: {
      "ui:field": "inlineField",
      checkHolderName: { "ui:md": 6 },
      checkNumber: { "ui:md": 6 },
    },
  },
};
